import React, { useContext, useState } from 'react';
import { CompanyContext } from '../../../../context/CompanyContext';
import { TagContext } from '../../../../context/TagContext';
import {
  CreateTagResponse,
  CreateTagVariables,
  TagMutations,
  UpdateTagResponse,
  UpdateTagVariables,
} from '../../../../graphql/tag.graphql';
import useMutation from '../../../../hooks/useMutation';
import { useToast } from '../../../../providers/ToastProvider';
import { EntityType, entityTypeToString } from '../../../../types/comment';
import { TagInput } from '../../../../types/tag';
import { Tag } from '../../../../types/tag/tag';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { Button, CancelButton } from '../../../components/Button';
import { ColorPicker } from '../../../components/ColorPicker';
import { DropdownSelect } from '../../../components/Dropdown';
import { ActionModal } from '../../../components/Modal';
import { TextInput } from '../../../components/TextInput';
import { ToastType } from '../../../components/Toast';

interface TagModalProps {
  tag: Tag | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function TagModal({ tag, open, setOpen }: TagModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { tags, setTags } = useContext(TagContext);

  const [input, setInput] = useState(
    new TagInput(tag || { companyId: currentCompany.id, type: EntityType.stockLocation }),
  );

  const handleActionCompleted = (tag: Tag) => {
    setTags(new Map(tags.set(tag.id, tag)));
    setOpen(false);
  };

  const [create, { loading: createLoading }] = useMutation<CreateTagResponse, CreateTagVariables>(TagMutations.create, {
    variables: { tags: [input.forCreate()] },
    onCompleted: response => {
      handleActionCompleted(response.createTag[0]);
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.tagCreatedSuccessfullyToastTitle).singular.upper,
      });
    },
  });

  const [update, { loading: updateLoading }] = useMutation<UpdateTagResponse, UpdateTagVariables>(TagMutations.update, {
    variables: { tags: [input.forUpdate()] },
    onCompleted: response => {
      handleActionCompleted(response.updateTag[0]);
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.tagUpdatedSuccessfullyToastTitle).singular.upper,
      });
    },
  });

  const loading = createLoading || updateLoading;

  const handleSave = () => {
    if (tag) {
      update();
    } else {
      create();
    }
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        onClick={() => handleSave()}
        text={t(tag ? TranslationKey.update : TranslationKey.create).singular.upper}
        loading={loading}
        disabled={!input.validate() || !input.hasChanges(tag)}
      />
    </div>
  );

  return (
    <ActionModal
      title={t(tag ? TranslationKey.updateTag : TranslationKey.createTag).singular.upper}
      footer={footer}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid grid-cols-2 gap-4'>
        <TextInput
          mandatory
          placeholder={t(TranslationKey.name).singular.label}
          label={t(TranslationKey.name).singular.label}
          value={input.name}
          onChange={value => setInput(input.withName(value))}
        />
        <DropdownSelect
          disabled={!!tag}
          label={t(TranslationKey.type).singular.label}
          placeholder={t(TranslationKey.type).singular.label}
          items={[EntityType.stockLocation, EntityType.pmd]}
          value={input.type}
          textBuilder={item => entityTypeToString(item)}
          onSelect={value => setInput(input.withType(value!))}
        />
        <ColorPicker
          color={input.color}
          onChange={color => setInput(input.withColor(color))}
          label={t(TranslationKey.color).singular.upper}
        />
      </div>
    </ActionModal>
  );
}
